import type { ReactNode } from 'react';
import DecisionPanel from '../DecisionPanel';
import ExplainThis from '../ExplainThis';
import Code, { CodeText } from '../Code';
import type { DecisionEnvelope, Disposition } from '../../types';

/**
 * SimulationOutput · the 4-part Move Brief.
 *
 * Per handoffV3 §10: "The most important UI artifact in the system. The thing
 * that turns the C2 from a dashboard into an operating tool."
 *
 * Every simulation sub-tab renders this with domain-specific content. The four
 * parts are: Recommendation / Evidence / Trade-off / Action Path. The
 * disposition click logs to the institutional memory (decision_log) and feeds
 * the learning loop.
 */

export interface SimEvidence {
  // One-line headline: "Expected margin +0.3pp, hero volume held within 2%."
  headline: string;
  // Confidence band: "p10–p90 of profit distribution".
  band?: { label: string; lo: string; hi: string };
  // Second-order effect on the binding constraint.
  bindingConstraintShift?: { from: string; to: string };
  // What we're betting on being right.
  exposedAssumption?: string;
  // War-game specific: the twin's most-likely counter move.
  twinCounter?: string;
  // Free-form extras (volume, share, hedge size, etc.) shown as a small grid.
  metrics?: Array<{ label: string; value: string; color?: 'red' | 'amber' | 'olive' | 'gold' | 'blue' | 'ink' }>;
}

export interface SimAlternative {
  move: string;                 // "Hold and observe"
  expected: string;             // "p50 −$0.8M, narrower band"
  rank: 2 | 3 | 4;
  whyLower: string;             // "Cedes shelf share to Pure Leaf"
}

export interface SimBlocked {
  move: string;                 // "Match Pure Leaf on price (89¢)"
  vetoId: string;               // "N1"
  vetoReason: string;           // "reprices_hero_can — breaks 99¢ floor"
}

export interface SimActionPath {
  owner: string;                // "CFO / Procurement"
  decideBy: string;             // ISO date or human date
  tripwire?: string;            // signal-and-value that triggers reversal
  rollback?: string;            // rollback move
}

export interface SimulationOutputProps {
  // 1 · Recommendation — one imperative move
  recommendation: string;
  recommendationDetail?: string;  // optional sub-line

  // 2 · Evidence
  evidence: SimEvidence;

  // 3 · Trade-off
  alternatives: SimAlternative[];
  blocked?: SimBlocked[];

  // 4 · Action Path
  actionPath: SimActionPath;

  // Disposition wiring
  driftId?: string;                                // optional — if bound to an existing packet
  // If provided, called when a disposition is selected. Throws → error toast.
  onDispose?: (d: Disposition, driftId?: string, decision?: DecisionEnvelope) => Promise<void>;
  // Override the "below disposition picker" copy
  ownerHint?: string;

  // Optional slot for sim-specific extras under the recommendation
  extraSlot?: ReactNode;
}

const COLOR_MAP: Record<string, string> = {
  red: 'var(--red)',
  amber: 'var(--amber)',
  gold: 'var(--gold)',
  olive: 'var(--olive)',
  blue: 'var(--blue)',
  ink: 'var(--ink)',
};

export default function SimulationOutput(props: SimulationOutputProps) {
  const {
    recommendation, recommendationDetail,
    evidence, alternatives, blocked = [],
    actionPath, driftId, onDispose, ownerHint, extraSlot,
  } = props;
  return (
    <div className="panel" style={{ background: 'var(--panel)', border: '1px solid var(--line)', padding: 0, overflow: 'hidden' }}>
      {/* Header strip */}
      <div style={{ background: 'var(--panel2)', borderBottom: '1px solid var(--line)', padding: '10px 18px' }}>
        <div className="row" style={{ gap: 10, alignItems: 'center' }}>
          <span className="tag amber" style={{ fontSize: 12, letterSpacing: '0.06em' }}>MOVE BRIEF</span>
          <span className="small faint" style={{ fontSize: 13 }}>Simulation output · one decision, four parts</span>
        </div>
      </div>

      <div style={{ padding: 18 }}>
        {/* 1 · RECOMMENDATION — hot, single statement */}
        <Section number="01" label="Recommendation" tone="hot">
          <div style={{ fontSize: 22, lineHeight: 1.35, color: 'var(--ink)', fontWeight: 600, marginBottom: recommendationDetail ? 6 : 0 }}>
            {recommendation}
          </div>
          {recommendationDetail && (
            <div className="small" style={{ color: 'var(--muted)', fontSize: 15, lineHeight: 1.45 }}>
              <CodeText text={recommendationDetail} compact />
            </div>
          )}
          {extraSlot && <div style={{ marginTop: 10 }}>{extraSlot}</div>}
        </Section>

        {/* 2 · EVIDENCE — numbers + bands + binding-constraint shift */}
        <Section number="02" label="Evidence · why this is the right move">
          <div style={{ fontSize: 15, color: 'var(--ink)', lineHeight: 1.5, marginBottom: 12 }}>
            <CodeText text={evidence.headline} compact />
          </div>
          {evidence.metrics && evidence.metrics.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(evidence.metrics.length, 4)}, 1fr)`, gap: 10, marginBottom: 12 }}>
              {evidence.metrics.map((m) => (
                <div key={m.label} style={{ padding: '10px 12px', background: 'var(--panel2)', border: '1px solid var(--line)', borderRadius: 6 }}>
                  <div className="small faint" style={{ fontSize: 12.5, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
                    {m.label}
                  </div>
                  <div className="mono" style={{ fontSize: 18, fontWeight: 700, color: COLOR_MAP[m.color ?? 'ink'] }}>
                    {m.value}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, fontSize: 15 }}>
            {evidence.band && (
              <Field label="Confidence band">
                <span className="mono">{evidence.band.label}</span>{' '}
                <span className="mono" style={{ color: 'var(--red)' }}>{evidence.band.lo}</span>
                <span style={{ color: 'var(--muted)' }}> → </span>
                <span className="mono" style={{ color: 'var(--olive)' }}>{evidence.band.hi}</span>
              </Field>
            )}
            {evidence.bindingConstraintShift && (
              <Field label="Binding-constraint shift">
                <span style={{ color: 'var(--red)' }}><CodeText text={evidence.bindingConstraintShift.from} compact /></span>
                <span style={{ color: 'var(--muted)' }}> → </span>
                <span style={{ color: 'var(--gold)' }}><CodeText text={evidence.bindingConstraintShift.to} compact /></span>
              </Field>
            )}
            {evidence.exposedAssumption && (
              <Field label="Exposed assumption · we bet this is right">
                <span style={{ color: 'var(--muted)' }}><CodeText text={evidence.exposedAssumption} compact /></span>
              </Field>
            )}
            {evidence.twinCounter && (
              <Field label="Twin's most-likely counter">
                <span style={{ color: 'var(--muted)' }}>{evidence.twinCounter}</span>
              </Field>
            )}
          </div>
        </Section>

        {/* 3 · TRADE-OFF — alternatives + BLOCKED visible */}
        <Section number="03" label="Trade-off · what we're not doing">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 8 }}>
            {alternatives.map((alt) => (
              <div key={alt.move} className="row" style={{ justifyContent: 'space-between', padding: '10px 12px', background: 'var(--panel2)', border: '1px solid var(--line)', borderRadius: 6, alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div className="row" style={{ gap: 8, marginBottom: 4 }}>
                    <span className="tag muted" style={{ fontSize: 12 }}>#{alt.rank}</span>
                    <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink)' }}>{alt.move}</span>
                  </div>
                  <div className="small" style={{ color: 'var(--ink)', fontSize: 14 }}>
                    <CodeText text={alt.expected} compact /> · <i style={{ color: 'var(--muted)' }}>why lower:</i> <CodeText text={alt.whyLower} compact />
                  </div>
                </div>
              </div>
            ))}
          </div>
          {blocked.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div className="small faint" style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6, color: 'var(--red)' }}>
                Blocked by non-objective vetoes
              </div>
              {blocked.map((b) => (
                <div key={b.move} className="row" style={{ padding: '10px 12px', background: 'var(--breach-bg, rgba(207,84,64,0.08))', border: '1px solid var(--red)', borderRadius: 6, marginBottom: 6, gap: 10, alignItems: 'flex-start' }}>
                  <span className="tag red" style={{ fontSize: 12, flexShrink: 0 }}>BLOCKED</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink)', marginBottom: 3 }}>{b.move}</div>
                    <div className="small" style={{ color: 'var(--red)', fontSize: 14 }}>
                      <Code id={b.vetoId} kind="non_objective" compact /> · <CodeText text={b.vetoReason} compact />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* 4 · ACTION PATH — owner, decide-by, disposition, tripwire, rollback */}
        <Section number="04" label="Action path · who decides, by when, how we back out">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14, fontSize: 15 }}>
            <Field label="Owner">
              <b style={{ color: 'var(--amber)' }}><CodeText text={actionPath.owner} compact /></b>
            </Field>
            <Field label="Decide by">
              <span className="mono">{actionPath.decideBy}</span>
            </Field>
            {actionPath.tripwire && (
              <Field label="Reversal trigger · reverses the move">
                <span style={{ color: 'var(--muted)' }}><CodeText text={actionPath.tripwire} compact /></span>
              </Field>
            )}
            {actionPath.rollback && (
              <Field label="Rollback move · if the trigger fires">
                <span style={{ color: 'var(--muted)' }}><CodeText text={actionPath.rollback} compact /></span>
              </Field>
            )}
          </div>
          <div>
            <DecisionPanel
              embedded
              owner={actionPath.owner}
              commit={(disposition, envelope) =>
                onDispose
                  ? onDispose(disposition, driftId, envelope)
                  : Promise.reject(new Error('No disposition endpoint is configured for this Move Brief.'))
              }
            />
          </div>
        </Section>

        {/* 'Explain this in plain language' on the Move Brief (spec 11.5; runs in the Simulation engine; marked AI aid, the brief stays of record). */}
        <ExplainThis
          engine_id={6}
          recommendation={recommendation}
          evidence={(evidence.metrics ?? []).map((m) => ({ label: m.label, value: m.value, provenance: 'modeled estimate' }))}
          tradeoffs={[evidence.headline, ...(alternatives ?? []).map((a) => `${a.move}: ${a.whyLower}`)]}
          constraints={blocked.map((b) => `Off the table: ${b.move}`)}
        />
      </div>
    </div>
  );
}

function Section({ number, label, tone, children }: { number: string; label: string; tone?: 'hot'; children: ReactNode }) {
  const bg = tone === 'hot' ? 'rgba(214,162,58,0.08)' : 'transparent';
  const border = tone === 'hot' ? '1px solid var(--amber)' : '1px solid var(--line)';
  return (
    <div style={{ background: bg, border, borderRadius: 8, padding: '12px 16px', marginBottom: 12 }}>
      <div className="row" style={{ gap: 8, marginBottom: 10, alignItems: 'center' }}>
        <span className="mono small faint" style={{ fontSize: 12 }}>{number}</span>
        <span style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.08em', color: tone === 'hot' ? 'var(--amber)' : 'var(--muted)', fontWeight: 700 }}>
          {label}
        </span>
      </div>
      {children}
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div className="small faint" style={{ fontSize: 12.5, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 15, color: 'var(--ink)' }}>{children}</div>
    </div>
  );
}
